/**
 * Progresso da criação assistida por IA.
 *
 * O job vive no servidor: fechar este modal (ou a aba inteira) não interrompe
 * nada. Por isso não há `beforeunload` — o usuário é informado de que pode sair.
 */

import { CheckCircle2, Circle, Loader2, Sparkles, X, AlertTriangle, RotateCcw } from 'lucide-react';
import { useJobIA } from '@/hooks/useJobIA';
import { useI18n } from '@/contexts/i18n-hook';
import type { IaJob, IaJobEtapaId } from '@/types/apreciacao-ia';

interface Props {
  aberto: boolean;
  jobId: string | null;
  machineName: string;
  onFechar: () => void;
  onConcluido?: (job: IaJob) => void;
}

/**
 * O rótulo da etapa chega pronto do servidor, em português. Os ids são um enum
 * estável, então o texto exibido sai do dicionário e o rótulo do servidor fica
 * só como reserva para uma etapa nova que o frontend ainda não conheça.
 */
const CHAVE_ETAPA: Record<IaJobEtapaId, string> = {
  'resolver-contexto': 'resolverContexto',
  'criar-apreciacao': 'criarApreciacao',
  'autofill-cabecalho': 'autofillCabecalho',
  'carregar-fotos': 'carregarFotos',
  'descrever-imagens': 'descreverImagens',
  'selecionar-destaque': 'selecionarDestaque',
  'decidir-partes': 'decidirPartes',
  'detalhar-partes': 'detalharPartes',
  'persistir-riscos': 'persistirRiscos',
  finalizar: 'finalizar',
};

function IconeEtapa({ status }: { status: 'pendente' | 'executando' | 'concluida' }) {
  if (status === 'concluida') return <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />;
  if (status === 'executando') return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-purple-600" />;
  return <Circle className="h-4 w-4 shrink-0 text-gray-300" />;
}

export function JobProgressModal({ aberto, jobId, machineName, onFechar, onConcluido }: Props) {
  const { t } = useI18n();
  const { job, erro, ativo, cancelar, retomar } = useJobIA(jobId, { onConcluido });

  if (!aberto || !jobId) return null;

  const falhou = job?.status === 'falhou';
  const interrompido = job?.status === 'interrompido';
  const cancelado = job?.status === 'cancelado';
  const concluido = job?.status === 'concluido';

  const rotuloEtapa = (id: IaJobEtapaId, doServidor: string) => {
    const chave = CHAVE_ETAPA[id];
    return chave ? t(`apreciacaoIa.etapa.${chave}`) : doServidor;
  };

  /** O servidor manda `Etapa — detalhe`; o detalhe é dado do job e fica como veio. */
  const rotuloProgresso = (j: IaJob) => {
    if (j.status === 'concluido') return t('apreciacaoIa.etapa.concluido');
    const separador = j.progresso.rotulo.indexOf(' — ');
    const detalhe = separador >= 0 ? j.progresso.rotulo.slice(separador) : '';
    return rotuloEtapa(j.etapaAtual, j.progresso.rotulo) + detalhe;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">{t('apreciacaoIa.progresso.titulo')}</h2>
              <p className="text-xs text-gray-500">{machineName}</p>
            </div>
          </div>
          <button
            onClick={onFechar}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label={t('comum.acao.fechar')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {erro && !job && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erro}</div>
          )}

          {job && (
            <>
              <div className="mb-4">
                <div className="mb-1 flex items-baseline justify-between text-sm">
                  <span className="font-medium text-gray-900">{rotuloProgresso(job)}</span>
                  <span className="text-gray-500">
                    {t('apreciacaoIa.progresso.percentual', { valor: job.percentual })}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      falhou ? 'bg-red-500' : concluido ? 'bg-green-600' : 'bg-purple-600'
                    }`}
                    style={{ width: `${job.percentual}%` }}
                  />
                </div>
              </div>

              <ol className="space-y-1.5">
                {job.etapas.map((e) => (
                  <li
                    key={e.id}
                    className={`flex items-start gap-2 text-sm ${
                      e.status === 'pendente' ? 'text-gray-400' : 'text-gray-800'
                    }`}
                  >
                    <IconeEtapa status={e.status} />
                    <span>{rotuloEtapa(e.id, e.rotulo)}</span>
                  </li>
                ))}
              </ol>

              {job.erro && (
                <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <strong>{t('apreciacaoIa.progresso.falhouEm', { etapa: job.erro.etapa })}</strong>{' '}
                  {job.erro.mensagem}
                </div>
              )}

              {interrompido && (
                <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {t('apreciacaoIa.progresso.interrompido')}
                </div>
              )}

              {job.avisos.length > 0 && (
                <details className="mt-4 rounded border border-gray-200 px-3 py-2">
                  <summary className="cursor-pointer text-xs font-semibold text-gray-700">
                    {t('apreciacaoIa.progresso.avisos', { total: job.avisos.length })}
                  </summary>
                  <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-gray-600">
                    {job.avisos.map((a, i) => (
                      <li key={i}>{a.mensagem}</li>
                    ))}
                  </ul>
                </details>
              )}

              {ativo && (
                <p className="mt-4 text-xs text-gray-500">{t('apreciacaoIa.progresso.podeFechar')}</p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-5 py-3">
          <div>
            {ativo && (
              <button
                onClick={() => void cancelar()}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                title={t('apreciacaoIa.progresso.cancelarTitulo')}
              >
                {t('apreciacaoIa.progresso.cancelar')}
              </button>
            )}
            {(falhou || interrompido) && (
              <button
                onClick={() => void retomar()}
                className="inline-flex items-center gap-1.5 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('apreciacaoIa.progresso.retomar')}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {cancelado && job?.apreciacaoId && (
              <span className="flex items-center gap-1 text-xs text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5" />
                {t('apreciacaoIa.progresso.rascunhoParcial')}
              </span>
            )}
            <button
              onClick={onFechar}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('comum.acao.fechar')}
            </button>
            {job?.apreciacaoId && (
              <a
                href={`/apreciacoes/${job.apreciacaoId}/editar`}
                className="rounded bg-purple-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-purple-700"
              >
                {t('apreciacaoIa.progresso.abrirApreciacao')}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
